import { Fragment, useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AccordionSection from "../../shared/accordionSection";

const TILE_SIZE = 84;
const MAX_THUMBNAILS = 4;
const TITLE_IMAGES = "Images";

const OrderImages = ({
  images = [],
  signatureUrl,
}: {
  images: string[];
  signatureUrl: string | null;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const hasContent = images.length > 0 || !!signatureUrl;

  const hasMore = images.length > MAX_THUMBNAILS;
  const inlineCount = hasMore
    ? MAX_THUMBNAILS - 1
    : Math.min(images.length, MAX_THUMBNAILS);
  const inlineImages = images.slice(0, inlineCount);
  const morePreview = images[inlineCount];

  const handleNext = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (activeIndex !== null && activeIndex < images.length - 1) {
        setActiveIndex(activeIndex + 1);
      }
    },
    [activeIndex, images.length]
  );

  const handlePrev = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (activeIndex !== null && activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    },
    [activeIndex]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeIndex === null) return;
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") setActiveIndex(null);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, handleNext, handlePrev]);

  const handleClose = () => {
    setOpen(false);
    setActiveIndex(null);
  };

  const ImageThumb = (src: string, alt: string, isMore?: boolean) => (
    <Box
      sx={{
        position: "relative",
        width: TILE_SIZE,
        height: TILE_SIZE,
        borderRadius: 1,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
      }}
      onClick={() => setOpen(true)}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          ...(isMore ? { filter: "brightness(0.6)" } : {}),
        }}
      />
      {isMore && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            color: "white",
          }}
        >
          +{images.length - inlineCount}
        </Box>
      )}
    </Box>
  );

  const content = (
    <Box mt={isMobile || !hasContent ? 0 : 1.5} p={isMobile ? 1 : 0}>
      {!isMobile && (
        <Typography
          fontWeight={600}
          mb={hasContent ? 2 : 1}
        >
          {TITLE_IMAGES}
        </Typography>
      )}

      {!hasContent ? (
        <Box sx={{ py: 0.5 }}>
          <Typography
            sx={{ color: "text.secondary" }}
          >
            There are no images or signatures available.
          </Typography>
        </Box>
      ) : (
        <>
          {images.length > 0 && (
            <Typography
              variant="subtitle2"
              fontWeight={600}
              mb={1}
              textAlign="center"
            >
              Delivery Images
            </Typography>
          )}

          <Stack
            direction="row"
            justifyContent={isMobile ? "center" : "space-between"}
            sx={{ mb: 2 }}
          >
            {inlineImages.map((src, i) => (
              <Fragment key={src}>
                {ImageThumb(src, `Delivery ${i + 1}`)}
              </Fragment>
            ))}
            {hasMore && ImageThumb(morePreview, "More", true)}
          </Stack>

          {signatureUrl && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={1}
                textAlign="center"
              >
                Signature
              </Typography>
              <Box
                onClick={() => {
                  setOpen(true);
                  setActiveIndex(-1);
                }}
                sx={{
                  height: 120,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={signatureUrl}
                  alt="Signature"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {activeIndex !== null ? (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveIndex(null)}
            >
              Back to Gallery
            </Button>
          ) : (
            "DELIVERY IMAGES"
          )}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box
          sx={{
            p: isMobile ? 1 : 3,
            position: "relative",
            bgcolor: activeIndex !== null ? "#1a1a1a" : "transparent",
          }}
        >
          {activeIndex !== null ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                position: "relative",
              }}
            >
              {(activeIndex ?? 0) > 0 && (
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 8,
                    color: "white",
                    bgcolor: "rgba(0,0,0,0.4)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <NavigateBeforeIcon fontSize="large" />
                </IconButton>
              )}

              <img
                src={activeIndex === -1 ? signatureUrl! : images[activeIndex]}
                alt="Enlarged"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />

              {activeIndex !== -1 && activeIndex < images.length - 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 8,
                    color: "white",
                    bgcolor: "rgba(0,0,0,0.4)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <NavigateNextIcon fontSize="large" />
                </IconButton>
              )}

              <Typography
                sx={{
                  position: "absolute",
                  bottom: -25,
                  color: "white",
                  opacity: 0.7,
                }}
              >
                {activeIndex === -1
                  ? "Signature"
                  : `Image ${activeIndex + 1} of ${images.length}`}
              </Typography>
            </Box>
          ) : (
            <ImageList variant="masonry" cols={isMobile ? 2 : 3} gap={12}>
              {images.map((src, i) => (
                <ImageListItem
                  key={src}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    overflow: "hidden",
                    "&:hover": { opacity: 0.8 },
                  }}
                  onClick={() => setActiveIndex(i)}
                >
                  <img src={src} alt={`Delivery ${i + 1}`} loading="lazy" />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>
      </Dialog>
    </Box>
  );

  return isMobile ? (
    <AccordionSection title={TITLE_IMAGES}>{content}</AccordionSection>
  ) : (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        height: hasContent ? "100%" : "auto",
        mt: 1.5,
      }}
    >
      {content}
    </Paper>
  );
};

export default OrderImages;
